import { loadGeojsonTypeAsync } from "./LoadGeojson";

export async function loadOsmTypeAsync({
  arrayBuffer = null,
  fileName,
  fileFormat = "osm",
  geojsonData = null,
  viewerRef,
  setMapObjects,
  defaultGeojsonStyle,
  basicGeojsonStyle,
  defaultOsmStyle,
  loadingDataForLayers,
}) {
  return new Promise(async (resolve, reject) => {
    try {
      const updateLoading = (patch) => {
        if (!loadingDataForLayers) return;
        const prev = loadingDataForLayers.current || {};
        loadingDataForLayers.current = { ...prev, ...patch };
      };

      updateLoading({
        show: true,
        step: "start",
        message: "Starting OSM loading...",
        current: 0,
        total: 0,
      });

      const normalizeKey = (v) =>
        String(v || "")
          .toLowerCase()
          .replace(/[-_\s]/g, "");

      const splitAndLoad = async (baseGeojson) => {
        const collectionsByType = new Map();
        const features = Array.isArray(baseGeojson.features) ? baseGeojson.features : [];

        for (const feature of features) {
          const typeVal = feature && feature.properties && feature.properties.type ? feature.properties.type : "_no_type_";

          if (!collectionsByType.has(typeVal)) {
            collectionsByType.set(typeVal, {
              type: "FeatureCollection",
              properties: {
                ...(baseGeojson.properties || {}),
                osmType: typeVal === "_no_type_" ? null : typeVal,
              },
              features: [],
            });
          }
          collectionsByType.get(typeVal).features.push(feature);
        }

        const typeArray = Array.from(collectionsByType.entries());
        const totalTypes = typeArray.length;

        updateLoading({
          step: "split-and-load",
          message: "Splitting features by type and loading into map...",
          current: 0,
          total: totalTypes,
        });

        let index = 0;
        for (const [typeVal, fc] of typeArray) {
          updateLoading({
            current: index + 1,
            currentType: typeVal,
          });

          let name = fileName;

          const sourceFile = fc && fc.properties && typeof fc.properties.source_file === "string" && fc.properties.source_file.trim() !== "" ? fc.properties.source_file.trim() : null;

          if (sourceFile) {
            name = sourceFile;
          } else {
            const rootType = (fc && fc.properties && fc.properties.type) || typeVal || "";

            let tempFL = "";
            switch (rootType) {
              case "centerline":
                tempFL = "lanelet";
                break;
              case "virtual":
                tempFL = "virtualline";
                break;
              case "curbstone":
                tempFL = "curbstone";
                break;
              case "line_thin":
                tempFL = "lanemarker";
                break;
              case "guardrail":
                tempFL = "guardrail";
                break;
              case "stop_line":
                tempFL = "stop_line";
                break;
              case "traffic_light":
                tempFL = "traffic_light";
                break;
              case "traffic_sign":
                tempFL = "traffic_sign";
                break;
              default:
                tempFL = "";
                break;
            }

            if (tempFL) {
              name = tempFL;
            } else {
              name = typeVal === "_no_type_" ? fileName : fileName + " - " + typeVal;
            }
          }

          const rootTypeForStyle = (fc && fc.properties && fc.properties.type) || typeVal || "";
          let tempFLStyle = "";
          switch (rootTypeForStyle) {
            case "centerline":
              tempFLStyle = "lanelet";
              break;
            case "virtual":
              tempFLStyle = "virtualline";
              break;
            case "curbstone":
              tempFLStyle = "curbstone";
              break;
            case "line_thin":
              tempFLStyle = "lanemarker";
              break;
            case "guardrail":
              tempFLStyle = "guardrail";
              break;
            case "stop_line":
              tempFLStyle = "stop_line";
              break;
            case "traffic_light":
              tempFLStyle = "traffic_light";
              break;
            case "traffic_sign":
              tempFLStyle = "traffic_sign";
              break;
            default:
              tempFLStyle = "";
              break;
          }

          let styleFromSource = null;
          const normSrc = normalizeKey(tempFLStyle);
          if (normSrc) {
            for (const key in basicGeojsonStyle) {
              if (!Object.prototype.hasOwnProperty.call(basicGeojsonStyle, key)) continue;
              const normKey = normalizeKey(key);
              if (!normKey) continue;
              if (normSrc.includes(normKey) || normKey.includes(normSrc)) {
                styleFromSource = basicGeojsonStyle[key];
                break;
              }
            }
          }

          let savedStyles = null;
          if (styleFromSource) {
            savedStyles = {
              style_stroke_width: styleFromSource.defaultLineWidth,
              style_fill_color: styleFromSource.defaultFillColor,
              style_outline_color: styleFromSource.defaultOutlineColor,
              style_type: styleFromSource.defaultStyle,
              style_pattern: styleFromSource.defaultPattern || styleFromSource.defaultPattren || styleFromSource.defaultPatternString,
              style_invisible_default: typeof styleFromSource.style_invisible_default === "boolean" ? styleFromSource.style_invisible_default : undefined,
            };
          }

          loadGeojsonTypeAsync({
            geojsonData: fc,
            fileName: name,
            viewerRef,
            setMapObjects,
            defaultGeojsonStyle,
            basicGeojsonStyle,
            defaultOsmStyle,
            savedStyles,
            allowCleanGeojson: false
          });

          index++;
        }
      };

      if (fileFormat === "geojson" && geojsonData) {
        updateLoading({
          step: "split-and-load",
          message: "Splitting GeoJSON features by type and loading into map...",
        });
        await splitAndLoad(geojsonData);
        updateLoading({
          step: "done",
          message: "GeoJSON loaded.",
          show: false,
        });
        return resolve();
      }

      if (fileFormat !== "osm") {
        updateLoading({
          step: "error",
          message: "Unsupported OSM file format.",
          show: false,
        });
        return reject(new Error("Unsupported OSM file format"));
      }

      updateLoading({
        step: "parse-xml",
        message: "Parsing OSM XML...",
      });

      const textContent = new TextDecoder("utf-8").decode(arrayBuffer);
      const xmlDoc = new DOMParser().parseFromString(textContent, "application/xml");

      updateLoading({
        step: "build-osm-json",
        message: "Building OSM JSON (nodes, ways, relations)...",
      });

      const nodeEls = Array.from(xmlDoc.querySelectorAll("node"));
      const wayEls = Array.from(xmlDoc.querySelectorAll("way"));
      const relationEls = Array.from(xmlDoc.querySelectorAll("relation"));

      const nodes = nodeEls.map((n) => ({
        id: n.getAttribute("id"),
        lat: n.getAttribute("lat"),
        lon: n.getAttribute("lon"),
        tags: Object.fromEntries(Array.from(n.querySelectorAll("tag")).map((t) => [t.getAttribute("k"), t.getAttribute("v")])),
      }));

      const ways = wayEls.map((w) => ({
        id: w.getAttribute("id"),
        ndRefs: Array.from(w.querySelectorAll("nd")).map((nd) => nd.getAttribute("ref")),
        tags: Object.fromEntries(Array.from(w.querySelectorAll("tag")).map((t) => [t.getAttribute("k"), t.getAttribute("v")])),
      }));

      const relations = relationEls.map((r) => ({
        id: r.getAttribute("id"),
        members: Array.from(r.querySelectorAll("member")).map((m) => ({
          type: m.getAttribute("type"),
          ref: m.getAttribute("ref"),
          role: m.getAttribute("role") || null,
        })),
        tags: Object.fromEntries(Array.from(r.querySelectorAll("tag")).map((t) => [t.getAttribute("k"), t.getAttribute("v")])),
      }));

      const osmJson = { nodes, ways, relations };

      updateLoading({
        step: "worker-convert",
        message: "Converting OSM JSON to GeoJSON in worker...",
      });

      const workerCode = `
        function buildMaps(osm) {
          const nodeMap = new Map();
          const wayMap = new Map();
          const relationMap = new Map();

          for (const n of osm.nodes || []) {
            const id = String(n.id);
            const lat = n.lat != null ? parseFloat(n.lat) : null;
            const lon = n.lon != null ? parseFloat(n.lon) : null;
            const eleTag = n.tags && n.tags.ele != null ? parseFloat(n.tags.ele) : null;
            nodeMap.set(id, {
              id,
              lat,
              lon,
              ele: Number.isFinite(eleTag) ? eleTag : null,
              tags: n.tags || {}
            });
          }

          for (const w of osm.ways || []) {
            const id = String(w.id);
            wayMap.set(id, {
              way: w,
              relations: []
            });
          }

          for (const r of osm.relations || []) {
            const id = String(r.id);
            relationMap.set(id, r);
          }

          return { nodeMap, wayMap, relationMap };
        }

        function processRelation(relationId, relationMap, wayMap, visited) {
          const id = String(relationId);
          if (!relationMap.has(id)) return;
          if (visited.has(id)) return;
          visited.add(id);

          const rel = relationMap.get(id);
          const relationTags = rel.tags || {};
          const relationType = relationTags.type || null;

          for (const mem of rel.members || []) {
            const memType = mem.type;
            const memRef = String(mem.ref);
            const memRole = mem.role || null;

            if (memType === "way") {
              if (!wayMap.has(memRef)) {
                wayMap.set(memRef, { way: null, relations: [] });
              }
              const entry = wayMap.get(memRef);
              entry.relations.push({
                id,
                tags: {
                  ...relationTags,
                  role: memRole,
                  memberType: memType,
                  relationType
                }
              });
            } else if (memType === "relation") {
              processRelation(memRef, relationMap, wayMap, visited);
            }
          }
        }

        function convertOSMJsonToGeojson(osm) {
          const { nodeMap, wayMap, relationMap } = buildMaps(osm);

          const nodesInRelations = new Set();
          const nodeRelationsMap = new Map();

          for (const rel of relationMap.values()) {
            const relId = String(rel.id);
            const relTags = rel.tags || {};

            for (const mem of rel.members || []) {
              if (mem.type === "node") {
                const nodeId = String(mem.ref);
                const role = mem.role || null;

                nodesInRelations.add(nodeId);

                if (!nodeRelationsMap.has(nodeId)) {
                  nodeRelationsMap.set(nodeId, []);
                }

                nodeRelationsMap.get(nodeId).push({
                  id: relId,
                  tags: relTags,
                  role
                });
              }
            }
          }

          for (const relId of relationMap.keys()) {
            processRelation(relId, relationMap, wayMap, new Set());
          }

          const features = [];

          for (const [wayId, wayData] of wayMap.entries()) {
            const w =
              wayData.way ||
              (osm.ways || []).find((x) => String(x.id) === wayId);
            if (!w) continue;

            const ndRefs = w.ndRefs || [];
            const coords = [];
            const ndObjs = [];
            const wayTags = { ...(w.tags || {}) };

            const relationsRaw = (wayData.relations || []).map((r) => ({
              id: r.id,
              tags: r.tags || {}
            }));

            if (Object.keys(wayTags).length === 0) {
              wayTags.note = "inferred from relation";
            }

            if (!wayTags.type) {
              const relWithType = relationsRaw.find(
                (rel) => rel.tags && rel.tags.relationType
              );
              if (relWithType) {
                wayTags.type = relWithType.tags.relationType;
              }
            }

            let relations = null;

            if (relationsRaw.length > 0) {
              relations = relationsRaw;
            }

            for (const ref of ndRefs) {
              const node = nodeMap.get(String(ref));
              if (node && node.lat != null && node.lon != null) {
                ndObjs.push(node);
                const z = node.ele != null ? node.ele : 0;
                coords.push([node.lon, node.lat, z]);
              }
            }

            if (!coords.length) continue;

            const baseProps = {
              wayId: wayId,
              relations,
              nodes: ndObjs
            };

            const flatProps = {
              ...baseProps,
              ...(wayTags || {})
            };

            features.push({
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: coords
              },
              properties: flatProps
            });
          }

          for (const nodeId of nodesInRelations) {
            const node = nodeMap.get(nodeId);
            if (!node || node.lat == null || node.lon == null) continue;

            const z = node.ele != null ? node.ele : 0;
            const relInfo = nodeRelationsMap.get(nodeId) || null;

            const baseProps = {
              nodeId: node.id,
              relations: relInfo,
              fromRelation: true
            };

            const flatProps = {
              ...baseProps,
              ...(node.tags || {})
            };

            features.push({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [node.lon, node.lat, z]
              },
              properties: flatProps
            });
          }

          return {
            type: "FeatureCollection",
            properties: {
              source: "OSM",
              version: "1.0",
              description: "OSM converted to GeoJSON with full way relations, node data, and relation nodes"
            },
            features
          };
        }

        self.onmessage = (e) => {
          try {
            const { osmJson } = e.data;
            const geojson = convertOSMJsonToGeojson(osmJson);
            self.postMessage({ success: true, geojson });
          } catch (err) {
            self.postMessage({
              success: false,
              error: err && err.message ? err.message : String(err)
            });
          }
        };
      `;

      const blob = new Blob([workerCode], { type: "application/javascript" });
      const workerUrl = URL.createObjectURL(blob);
      const worker = new Worker(workerUrl);

      worker.onmessage = async (evt) => {
        const { success, geojson, error } = evt.data;

        if (!success) {
          updateLoading({
            step: "error",
            message: "Error while converting OSM to GeoJSON.",
            show: false,
          });
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
          return reject(new Error(error || "OSM to GeoJSON worker error"));
        }

        try {
          await splitAndLoad(geojson);
          updateLoading({
            step: "done",
            message: "OSM loaded successfully.",
            show: false,
          });
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
          resolve();
        } catch (e) {
          updateLoading({
            step: "error",
            message: "Error while loading GeoJSON into map.",
            show: false,
          });
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
          reject(e);
        }
      };

      worker.onerror = (err) => {
        updateLoading({
          step: "error",
          message: "Worker error while converting OSM to GeoJSON.",
          show: false,
        });
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
        reject(err);
      };

      worker.postMessage({ osmJson });
    } catch (err) {
      if (loadingDataForLayers) {
        loadingDataForLayers.current = {
          ...(loadingDataForLayers.current || {}),
          step: "error",
          message: "Unexpected error while loading OSM.",
          show: false,
        };
      }
      reject(err);
    }
  });
}
